import type { CSSProperties } from "react";
import { ChevronsUpDown, X } from "lucide-react";
import { useAuthStore } from "@/stores/auth";
import { usePreferencesStore } from "@/stores/preferences";
import { initialsFor, paletteForOrg } from "@/lib/orgColor";
import { Icon } from "@/components/ds";

/**
 * Org context banner — confirms which org you're in. Uses the calm, fluid
 * mesh-blob animation (three slow radial blobs floating with ease-in-out
 * alternation — no hard loop seam), restored per feedback over the linear
 * flowing-gradient. Blob keyframes live in index.css; honours
 * prefers-reduced-motion there. Dismissed via the preferences store (restore
 * from Settings → Display). "Switch org" re-opens the sidebar org switcher.
 */
export function OrgBanner({ onSwitchOrg }: { onSwitchOrg?: () => void } = {}) {
  const activeOrgId = useAuthStore((s) => s.activeOrganizationId);
  const organizations = useAuthStore((s) => s.organizations);
  const showOrgBanner = usePreferencesStore((s) => s.showOrgBanner);
  const setShowOrgBanner = usePreferencesStore((s) => s.setShowOrgBanner);

  const org = organizations.find((o) => o.id === activeOrgId) ?? null;
  if (!org || !showOrgBanner) return null;

  const palette = paletteForOrg(org.id);
  const initials = initialsFor(org.name);

  const openSwitcher = () => {
    if (onSwitchOrg) {
      onSwitchOrg();
      return;
    }
    const dropdown = document.querySelector<HTMLButtonElement>("[data-org-switcher]");
    if (dropdown) {
      dropdown.scrollIntoView({ behavior: "smooth", block: "nearest" });
      dropdown.click();
    }
  };

  const blob = (color: string): CSSProperties => ({
    zIndex: -1,
    background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
  });

  return (
    <div
      className="orgbanner mb-5"
      style={{ background: palette.base }}
      role="complementary"
      aria-label={`Active organization: ${org.name}`}
    >
      {/* calm floating mesh blobs (animation in index.css) */}
      <span className="org-banner-blob org-banner-blob-1" style={blob(palette.blobs[0])} aria-hidden="true" />
      <span className="org-banner-blob org-banner-blob-2" style={blob(palette.blobs[1])} aria-hidden="true" />
      <span className="org-banner-blob org-banner-blob-3" style={blob(palette.blobs[2])} aria-hidden="true" />

      <span className="orgbanner__av">{initials}</span>
      <div className="orgbanner__txt">
        <div className="orgbanner__name">{org.name}</div>
        <div className="orgbanner__sub">
          You're working in this organisation{org.is_personal ? " · Personal" : ""}
        </div>
        <div className="orgbanner__mobile-plan">
          {org.is_personal ? "Personal workspace" : "Team workspace"}
        </div>
      </div>
      {organizations.length > 1 && (
        <button type="button" className="orgbanner__switch" onClick={openSwitcher}>
          <span className="orgbanner__switch-label">Switch org</span>
          <Icon icon={ChevronsUpDown} size={16} className="orgbanner__switch-icon" />
        </button>
      )}
      <button
        type="button"
        className="orgbanner__close"
        aria-label="Hide organization banner"
        onClick={() => setShowOrgBanner(false)}
      >
        <Icon icon={X} size={16} />
      </button>
    </div>
  );
}
